export type FeatureStatus = 'completed' | 'in-progress' | 'planned' | 'backlog';
export type FeatureCategory = 'Trading' | 'Platform' | 'Broker' | 'Automation' | 'Analytics' | 'Account';

export interface TimelineFeature {
  id: string;
  title: string;
  description: string;
  status: FeatureStatus;
  date: string;
  category: FeatureCategory;
  requester?: string;
}

export const statusConfig: Record<FeatureStatus, { label: string; dotColor: string; textColor: string }> = {
  completed: {
    label: 'Completed',
    dotColor: 'bg-green-500',
    textColor: 'text-green-700 dark:text-green-300',
  },
  'in-progress': {
    label: 'In Progress',
    dotColor: 'bg-blue-500',
    textColor: 'text-blue-700 dark:text-blue-300',
  },
  planned: {
    label: 'Planned',
    dotColor: 'bg-gray-400 dark:bg-gray-500',
    textColor: 'text-gray-600 dark:text-gray-400',
  },
  backlog: {
    label: 'Backlog',
    dotColor: 'bg-gray-300 dark:bg-gray-600',
    textColor: 'text-gray-500 dark:text-gray-500',
  },
};

export const categoryColors: Record<FeatureCategory, { bg: string; text: string }> = {
  Trading: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
  Platform: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300' },
  Broker: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300' },
  Automation: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300' },
  Analytics: { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-300' },
  Account: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
};

export const timelineFeatures: TimelineFeature[] = [
  {
    id: 'dte-entry-selection',
    title: 'DTE-Based Entry Selection',
    description: 'Schedule intraday strategies by days-to-expiry (D0–D4) instead of fixed weekdays. Pick the contract\'s expiry day, the day before, or N days before — strategies auto-adapt when holidays shift an expiry, no manual updates needed. Supported on NIFTY and SENSEX weekly strategies.',
    status: 'completed',
    date: '2026-05-30',
    category: 'Trading',
  },
  {
    id: 'inline-strategy-editor',
    title: 'New Strategy Editor (Inline + Leg Matrix)',
    description: 'The full-screen strategy configuration dialog is replaced with a streamlined inline editor that opens directly inside the basket — no more modals, no more scrolling. Each leg becomes a row in a scannable matrix (one column per parameter: strike, type, lots, stop loss, target, etc.) and per-feature column-toggle chips reveal advanced columns (Trail SL, Wait & Trade, SL Re-Entry, TP Re-Exec) for every leg at once. A persistent config strip handles entry / exit / risk settings without scrolling, with an unsaved-edit guard so you never lose work mid-edit.',
    status: 'completed',
    date: '2026-05-29',
    category: 'Platform',
  },
  {
    id: 'basket-strategy-reorder',
    title: 'Drag-and-Drop Reorder',
    description: 'Reorder baskets and strategies on the Baskets page with drag handles (desktop) or up/down arrows (mobile). The sort dropdown gains a new "Custom" option which becomes the default. Visual ordering — execution still fires at your scheduled entry times.',
    status: 'completed',
    date: '2026-05-13',
    category: 'Platform',
  },
  {
    id: 'basket-import-export',
    title: 'Basket Import / Export & Bulk Copy',
    description: 'Export a basket and any subset of its strategies as a portable JSON bundle. Import via a 3-step wizard (Upload → Preview → Confirm) with selective import, inline rename, and environment validation. Plus: bulk-copy strategies between baskets within one account in a single click.',
    status: 'completed',
    date: '2026-05-08',
    category: 'Platform',
  },
  {
    id: 'strategy-execution-id',
    title: 'Per-Execution Traceability',
    description: 'Every firing of a basket allocation gets a unique execution ID stamped on all spawned positions. A new "Show full execution" toggle on the Schedule page reveals every chain-spawn (re-entry, re-cost, TP-reexec, manual entry) tied to a single firing — full traceability from one click.',
    status: 'completed',
    date: '2026-05-05',
    category: 'Trading',
  },
  {
    id: 'exit-all',
    title: 'Exit All & Manual Exit',
    description: 'One-click Exit All button to flatten every open position across all baskets and brokers. Manually-exited positions are tagged so they’re excluded from re-entry / re-execute chains.',
    status: 'completed',
    date: '2026-05-04',
    category: 'Trading',
  },
  {
    id: 'next-weekly-monthly-expiry',
    title: 'Next Weekly & Monthly Expiries',
    description: 'Trade next-weekly and next-monthly expiry contracts in addition to current-week and current-month. Strike rules, DTE projections, and broker symbol formats all honour the chosen expiry type.',
    status: 'completed',
    date: '2026-05-03',
    category: 'Trading',
  },
  {
    id: 'trailing-stop-loss',
    title: 'Trailing Stop Loss',
    description: 'Per-leg trailing stop loss that automatically tightens your SL as price moves in your favor. Anchors at first SL placement and tracks candle-based extremes — locks in profits while limiting downside.',
    status: 'completed',
    date: '2026-05-03',
    category: 'Trading',
  },
  {
    id: 'target-profit',
    title: 'Target Profit (Leg-level)',
    description: 'Set a profit target per leg in points or percentage. When hit, the leg exits automatically — with TP Re-Execute support to reopen the leg if conditions reset.',
    status: 'completed',
    date: '2026-04-25',
    category: 'Trading',
  },
  {
    id: 'stop-loss-re-execute',
    title: 'Stop Loss Re-Execute',
    description: 'After a Stop Loss hit, immediately re-run the leg\'s strike selection against the current market and re-enter at the freshly-resolved strike\'s LTP. Honours the leg\'s full config — strike rule, stop loss percent, etc. Counter-bounded (1–5).',
    status: 'completed',
    date: '2026-04-18',
    category: 'Trading',
  },
  {
    id: 'stop-loss-re-entry',
    title: 'Stop Loss Re-Entry',
    description: 'After a Stop Loss hit, automatically re-enter the same leg when a 1-minute candle closes back past the original entry price. Retries are counter-bounded (1–5), anchored to the original price, and survive overnight for BTST / POSITIONAL strategies.',
    status: 'completed',
    date: '2026-04-18',
    category: 'Trading',
  },
  {
    id: 'stop-loss-re-cost',
    title: 'Stop Loss Re-Cost',
    description: 'After a Stop Loss hit, place an immediate SL-trigger order at the original entry price — the broker holds it and fills as soon as premium returns. Counter-bounded, overnight-preserved for NRML strategies, and ends at the strategy’s scheduled exit.',
    status: 'completed',
    date: '2026-04-18',
    category: 'Trading',
  },
  {
    id: 'execution-schedule-view',
    title: 'Execution Schedule View',
    description: 'New page that projects every basket’s upcoming entries and exits across the calendar — DTE overlay, Classic / Pro views, per-column filters, health indicators, and a Live tab for today’s activity.',
    status: 'completed',
    date: '2026-04-21',
    category: 'Platform',
  },
  {
    id: 'copy-strategy-shipped',
    title: 'Copy Strategy',
    description: 'Duplicate any existing strategy with all its legs and settings — a one-click action in basket management to spin up variations without rebuilding from scratch.',
    status: 'completed',
    date: '2026-04-21',
    category: 'Trading',
    requester: 'Neelesh',
  },
  {
    id: 'marketplace-backtest-stats',
    title: 'Marketplace Backtest Stats',
    description: 'Admins can upload a backtest CSV per listing; the platform computes performance stats (CAGR, total return, win rate, Sharpe, drawdown, net P&L) and shows them on browse cards and detail pages.',
    status: 'completed',
    date: '2026-04-20',
    category: 'Trading',
  },
  {
    id: 'broader-index-coverage',
    title: 'SENSEX, BANKEX & MIDCPNIFTY Support',
    description: 'Trade BSE indices (SENSEX, BANKEX) on BFO and MIDCPNIFTY on NFO — strike resolution, broker symbol formats, and lot sizes all handled per index.',
    status: 'completed',
    date: '2026-04-15',
    category: 'Trading',
  },
  {
    id: 'wait-and-trade',
    title: 'Wait-and-Trade Entry',
    description: 'Place trades only when your target trigger price is hit first, giving you precise entry timing with overnight order support.',
    status: 'completed',
    date: '2026-04-04',
    category: 'Trading',
  },
  {
    id: 'order-lifecycle',
    title: 'Smart Order Management',
    description: 'Automatic stop-loss placement after entry, real-time order tracking, and seamless sync with your broker.',
    status: 'completed',
    date: '2026-04-03',
    category: 'Trading',
  },
  {
    id: 'system-monitoring',
    title: 'System Health Monitoring',
    description: 'Live monitoring dashboard to ensure all trading operations run smoothly and reliably.',
    status: 'completed',
    date: '2026-04-02',
    category: 'Platform',
  },
  {
    id: 'faster-broker-sessions',
    title: 'Faster Broker Sessions',
    description: 'Improved broker connection speed with smarter session handling for quicker order execution.',
    status: 'completed',
    date: '2026-04-02',
    category: 'Broker',
  },
  {
    id: 'position-tracking',
    title: 'Live Position Tracking',
    description: 'Track all your open positions in real-time with profit/loss updates across multiple broker accounts.',
    status: 'completed',
    date: '2026-03-30',
    category: 'Trading',
  },
  {
    id: 'auto-exit-entry',
    title: 'Automated Daily Exit & Entry',
    description: 'Positions are exited based on your strategy exit rules, and protective orders are placed at market open — fully hands-free.',
    status: 'completed',
    date: '2026-03-29',
    category: 'Automation',
  },
  {
    id: 'multi-broker',
    title: 'Multi-Broker Support',
    description: 'Connect and trade through Zerodha, Angel One, Finvasia, or Zebu — all from a single dashboard.',
    status: 'completed',
    date: '2026-03-25',
    category: 'Broker',
  },
  {
    id: 'strategy-marketplace',
    title: 'Strategy Marketplace',
    description: 'Browse and subscribe to exchange-approved trading strategies. One click to start following.',
    status: 'completed',
    date: '2026-03-22',
    category: 'Trading',
  },
  {
    id: 'platform-launch',
    title: 'Platform Launch',
    description: 'Quantleap Analytics goes live with basket-based algo trading, strategy configuration, and secure user accounts.',
    status: 'completed',
    date: '2026-03-19',
    category: 'Platform',
  },
  {
    id: 'zebu-login',
    title: 'Zebu One-Click Login',
    description: 'Zebu users can log in directly with their broker credentials — no separate registration needed.',
    status: 'completed',
    date: '2026-04-04',
    category: 'Account',
  },
  {
    id: 'beta-program',
    title: 'Beta Access Program',
    description: 'Invite-only early access for new users to try the platform before public launch.',
    status: 'completed',
    date: '2026-04-05',
    category: 'Platform',
  },
  {
    id: 'orb-strategy',
    title: 'Opening Range Breakout',
    description: 'New strategy type that trades breakouts from the opening price range with customizable time windows.',
    status: 'completed',
    date: '2026-04-05',
    category: 'Trading',
  },
  {
    id: 'strategy-target-profit',
    title: 'Strategy Target Profit',
    description: 'Strategy-level MTM target — when the combined P&L of all legs hits this target, the entire strategy exits.',
    status: 'in-progress',
    date: 'Apr 2026',
    category: 'Trading',
  },
  {
    id: 'strategy-stop-loss',
    title: 'Strategy Stop Loss',
    description: 'Strategy-level MTM stop loss — when the combined loss of all legs hits this threshold, the entire strategy exits.',
    status: 'in-progress',
    date: 'Apr 2026',
    category: 'Trading',
  },
  {
    id: 'analytics-dashboard',
    title: 'Performance Analytics',
    description: 'Detailed reports on your trading performance — win rate, P&L trends, strategy comparisons, basket→strategy drill-down, and a calendar heatmap. Refreshed nightly per broker account.',
    status: 'completed',
    date: '2026-05-11',
    category: 'Analytics',
  },
  {
    id: 'delta-based-entry',
    title: 'Delta-Based Entry',
    description: 'Select strikes by option delta instead of fixed points or premium, enabling more precise risk-defined entries that adapt to market conditions.',
    status: 'backlog',
    date: '',
    category: 'Trading',
    requester: 'Saket',
  },
];

export const getFeaturesByStatus = (status: FeatureStatus): TimelineFeature[] =>
  timelineFeatures.filter((f) => f.status === status);
